import React from "react";
import {
  XAxis,
  YAxis,
  Line,
  ComposedChart,
  Customized,
  ResponsiveContainer,
  Tooltip,
  Label,
  TooltipProps,
} from "recharts";
import {
  BusinessTrendChartData,
  TimeFrame,
} from "../../services/interfaces/IBusinessCostTrend";
import {
  dateFormatterToMMMYY,
  dateFormatterToMMDD,
} from "../../utils/formatter";

interface BusinessCostCompChartProps {
  data: BusinessTrendChartData[];
  id: string;
  timeFrame: TimeFrame;
  commodity: string | null;
  baseSKU: string | null;
}

interface PPVAreaLayerProps {
  data?: BusinessTrendChartData[];
  id: string;
  offset?: { left: number; top: number; width: number; height: number };
  tooltipTicks?: Array<{ coordinate: number }>;
  tooltipAxisBandSize?: number;
  yAxisMap?: Record<string, { scale: (value: number) => number }>;
}

const PPVAreaLayer = ({
  data = [],
  id,
  offset,
  tooltipTicks = [],
  tooltipAxisBandSize = 0,
  yAxisMap,
}: PPVAreaLayerProps) => {
  const yScale = yAxisMap?.[0]?.scale;

  if (!offset || !yScale || tooltipTicks.length < data.length) {
    return null;
  }

  const getX = (index: number) =>
    tooltipTicks[index].coordinate + tooltipAxisBandSize / 2;
  const getPath = (points: Array<[number, number]>) =>
    `${points
      .map(([x, y], index) => `${index === 0 ? "M" : "L"}${x},${y}`)
      .join(" ")} Z`;

  const areas: React.ReactNode[] = [];

  for (let index = 0; index < data.length - 1; index += 1) {
    const current = data[index];
    const next = data[index + 1];
    const values = [
      current.business_cost,
      current.po_cost,
      next.business_cost,
      next.po_cost,
    ];

    if (!values.every(Number.isFinite)) {
      continue;
    }

    const x1 = getX(index);
    const x2 = getX(index + 1);
    const businessY1 = yScale(current.business_cost);
    const businessY2 = yScale(next.business_cost);
    const poY1 = yScale(current.po_cost);
    const poY2 = yScale(next.po_cost);
    const difference1 = current.business_cost - current.po_cost;
    const difference2 = next.business_cost - next.po_cost;
    const crosses = difference1 * difference2 < 0;

    if (!crosses) {
      areas.push(
        <path
          key={index}
          d={getPath([
            [x1, businessY1],
            [x2, businessY2],
            [x2, poY2],
            [x1, poY1],
          ])}
          fill={difference1 + difference2 >= 0 ? "#d8e9b8" : "#fccccc"}
        />,
      );
      continue;
    }

    const crossingRatio = difference1 / (difference1 - difference2);
    const crossingX = x1 + crossingRatio * (x2 - x1);
    const crossingValue =
      current.business_cost +
      crossingRatio * (next.business_cost - current.business_cost);
    const crossingY = yScale(crossingValue);

    areas.push(
      <path
        key={`${index}-before`}
        d={getPath([
          [x1, businessY1],
          [crossingX, crossingY],
          [x1, poY1],
        ])}
        fill={difference1 > 0 ? "#d8e9b8" : "#fccccc"}
      />,
      <path
        key={`${index}-after`}
        d={getPath([
          [crossingX, crossingY],
          [x2, businessY2],
          [x2, poY2],
        ])}
        fill={difference2 > 0 ? "#d8e9b8" : "#fccccc"}
      />,
    );
  }

  return (
    <g clipPath={`url(#${id}-ppv-clip)`}>
      <defs>
        <clipPath id={`${id}-ppv-clip`}>
          <rect
            x={offset.left}
            y={offset.top}
            width={offset.width}
            height={offset.height}
          />
        </clipPath>
      </defs>
      {areas}
    </g>
  );
};

export const BusinessCostCompChart: React.FC<BusinessCostCompChartProps> = ({
  data,
  id,
  timeFrame,
  commodity,
  baseSKU,
}) => {
  const BusinessCostTrendTooltip: React.FC<TooltipProps<string, number>> = ({
    active,
    payload,
    label,
  }) => {
    const formatCurrencyValue = (value: any) => {
      const numericValue = Number(value);
      if (value !== null && value !== "" && !Number.isNaN(numericValue)) {
        return `$${numericValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }
      return value;
    };

    if (active && payload && payload.length) {
      const filterPayload = payload.filter((item) => item.dataKey !== "range");
      const tooltipOrder: { dataKey: string; label: string }[] = [
        { dataKey: "sell_price", label: "Sales Price" },
        { dataKey: "business_cost", label: "Board Cost" },
        { dataKey: "po_cost", label: "PO Cost" },
      ];
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #bebebe",
          }}
        >
          <p>
            Date:{" "}
            {timeFrame === TimeFrame.MONTH
              ? dateFormatterToMMMYY(label)
              : dateFormatterToMMDD(label)}
          </p>
          {tooltipOrder.map((orderItem) => {
            const item = filterPayload.find(
              (p) => p.dataKey === orderItem.dataKey,
            );
            if (!item) {
              return null;
            }
            return (
              <p
                key={orderItem.dataKey}
                style={{ color: item.color, textTransform: "capitalize" }}
              >
                {orderItem.label} : {formatCurrencyValue(item.value)}
              </p>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const CustomYAxisLabel = ({ viewBox, value }: any) => {
    const { x, y, width, height } = viewBox;
    const lines = value.split("\n");
    const cx = x;
    const cy = height / 2;
    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        transform={`rotate(-90, ${cx}, ${cy})`}
        fill="black"
        fontSize={"0.6vw"}
        fontWeight={600}
      >
        {lines.map((line: any, index: number) => (
          <tspan
            fill={index === 0 ? "#86BC25" : "black"}
            key={index}
            x={0}
            dy={`${index * 1.3}em`}
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        margin={{
          top: 10,
          right: 20,
          bottom: 2,
          left: 20,
        }}
        data={data}
      >
        <XAxis
          dataKey="date"
          tickLine={false}
          tickCount={0}
          strokeWidth={1}
          axisLine={false}
          hide
          padding={{ left: 20 }}
        />
        <YAxis
          // tickCount={7}
          tickLine={true}
          allowDecimals={false}
          allowDataOverflow={false}
          interval="preserveStartEnd"
          strokeWidth={1}
          // domain={[Math.floor(lowerBound), Math.ceil(upperBound)]}
          domain={([dataMin, dataMax]) => {
            const divFactor = dataMax > 100 ? 20 : dataMax > 1 ? 10 : 0.2;
            return [
              Math.floor(dataMin / divFactor) * divFactor,
              Math.ceil(dataMax / divFactor) * divFactor,
            ];
          }}
        >
          <Label
            className="text-fs12"
            style={{
              textAnchor: "middle",
              fill: "black",
            }}
            angle={-90}
            value={`${baseSKU}\n($/cft)`}
            content={<CustomYAxisLabel />}
            position={"insideLeft"}
          />
        </YAxis>
        <Tooltip content={<BusinessCostTrendTooltip />} />

        <Customized component={<PPVAreaLayer data={data} id={id} />} />

        <Line
          type="linear"
          dataKey="po_cost"
          strokeWidth={2}
          stroke="#E49257"
          dot={{ stroke: "#E49257", strokeWidth: 2, r: 2, fill: "#E49257" }}
        />
        <Line
          type="linear"
          dataKey="business_cost"
          strokeWidth={2}
          stroke="#012169"
          dot={{ stroke: "#012169", strokeWidth: 2, r: 2, fill: "#012169" }}
        />
        <Line
          type="linear"
          dataKey="sell_price"
          strokeWidth={2}
          stroke="#0097A9"
          dot={{ stroke: "#0097A9", strokeWidth: 2, r: 2, fill: "#0097A9" }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
